import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginSchema, magicLinkSchema } from '../../lib/validators/authSchemas';
import { useLoginMutation, useRequestMagicLinkMutation } from '../../features/auth/authApi';
import { selectBootstrapWarning, setBootstrapWarning } from '../../features/auth/authSlice';
import { getRoleHomePath } from '../../routes/roleHome';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { useToast } from '../../components/ui/Toast';
import Input from '../../components/ui/Input';
import PasswordInput from '../../components/ui/PasswordInput';
import Button from '../../components/ui/Button';

function PasswordLoginForm({ onSuccess2fa }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [login, { isLoading }] = useLoginMutation();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (values) => {
    try {
      const result = await login(values).unwrap();

      // Admin / Super Admin accounts get a 2FA challenge instead of an
      // immediate session — see docs "Two-factor authentication". The
      // live API actually sends this as `requires2fa` (no underscore),
      // which differs from the published OpenAPI schema — checking both
      // so this keeps working if that ever gets corrected server-side.
      if (result?.requires2fa || result?.requires_2fa) {
        onSuccess2fa(values.email);
        return;
      }

      const roles = result?.user?.roles ?? result?.user?.role ?? [];
      const target = location.state?.from?.pathname || getRoleHomePath(roles);
      navigate(target, { replace: true });
    } catch (err) {
      if (err?.data?.errors?.email?.[0]) {
        setError('root', { message: err.data.errors.email[0] });
        return;
      }

      const status = err?.status;

      // 403 covers two distinct cases the API doesn't otherwise
      // distinguish — "account suspended" and "email not yet verified" —
      // so the message text itself is the only signal for which one this
      // is. Unverified is actionable (send them to verify), suspended
      // isn't, so only the former redirects.
      if (status === 403) {
        const message = authErrorMessage(err, { fallback: 'Account suspended, or email not yet verified.' });
        const isUnverified = /verif/i.test(message);

        toast.push({
          tone: 'warning',
          title: isUnverified ? 'Email not verified' : 'Account suspended',
          message,
        });

        if (isUnverified) {
          navigate('/verify-email', { state: { email: values.email, autoResend: true } });
        }
        return;
      }

      setError('root', {
        message: authErrorMessage(err, { fallback: 'Login failed. Check your credentials.' }),
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <Input label="Email address" type="email" autoComplete="email" error={errors.email?.message} {...register('email')} />
      <PasswordInput
        label="Password"
        autoComplete="current-password"
        error={errors.password?.message}
        {...register('password')}
      />

      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

      <div className="flex justify-end">
        <Link to="/forgot-password" className="text-sm text-accent hover:underline">
          Forgot password?
        </Link>
      </div>

      <Button type="submit" className="w-full" isLoading={isLoading}>
        Log in
      </Button>
    </form>
  );
}

function MagicLinkForm() {
  const navigate = useNavigate();
  const [requestMagicLink, { isLoading }] = useRequestMagicLinkMutation();
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(magicLinkSchema) });

  const onSubmit = async (values) => {
    setError('');
    try {
      // Always 200 regardless of whether the email is registered/eligible —
      // enumeration-safe by design, so a real failure here means something
      // is actually wrong server-side, not "that email doesn't exist".
      await requestMagicLink(values).unwrap();
      navigate('/magic-link/sent', { state: { email: values.email } });
    } catch (err) {
      setError(authErrorMessage(err, { notFound: "Magic-link sign-in isn't available right now — try password login instead." }));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <Input label="Email address" type="email" autoComplete="email" error={errors.email?.message} {...register('email')} />
      <p className="text-xs text-foreground-subtle -mt-1">
        We'll email a one-time sign-in link — no password needed. Investor accounts only.
      </p>
      {error && <p className="text-sm text-danger">{error}</p>}
      <Button type="submit" className="w-full" isLoading={isLoading}>
        Send magic link
      </Button>
    </form>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const bootstrapWarning = useSelector(selectBootstrapWarning);
  const [mode, setMode] = useState('password'); // 'password' | 'magic-link'

  const handle2fa = (email) => navigate('/login/2fa', { state: { email } });

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Welcome back</h1>
      <p className="text-sm text-foreground-muted mb-6">Log in to your Cavan Capital account.</p>

      {location.state?.passwordReset && (
        <p className="text-sm text-accent bg-accent-soft rounded-lg px-3.5 py-2.5 mb-4">
          Password reset — log in with your new password.
        </p>
      )}

      {bootstrapWarning === 'profile_unavailable' && (
        <p className="text-sm text-warning bg-warning-soft rounded-lg px-3.5 py-2.5 mb-4">
          We couldn't fully restore your last session — please log in again.
          <button
            type="button"
            onClick={() => dispatch(setBootstrapWarning(null))}
            className="block mt-1 text-xs text-foreground-subtle hover:text-foreground underline"
          >
            Dismiss
          </button>
        </p>
      )}

      <div className="flex gap-1 p-1 rounded-xl bg-background-elevated mb-6">
        <button
          type="button"
          onClick={() => setMode('password')}
          className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
            mode === 'password' ? 'bg-accent text-background shadow-glow-sm' : 'text-foreground-muted hover:text-foreground'
          }`}
        >
          Password
        </button>
        <button
          type="button"
          onClick={() => setMode('magic-link')}
          className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
            mode === 'magic-link' ? 'bg-accent text-background shadow-glow-sm' : 'text-foreground-muted hover:text-foreground'
          }`}
        >
          Magic link
        </button>
      </div>

      {mode === 'password' ? <PasswordLoginForm onSuccess2fa={handle2fa} /> : <MagicLinkForm />}

      <p className="text-sm text-foreground-muted mt-6 text-center">
        Don't have an account?{' '}
        <Link to="/register" className="text-accent hover:underline">
          Sign up
        </Link>
      </p>
    </div>
  );
}
